// react_frontend/src/Components/Modules/PurchaseSetting/StockReturn.js
import React, { useState, useEffect, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { useNavigate } from "react-router-dom";
// RSuite Components
import { Button, Input, Badge, Loader, Message, toaster } from "rsuite";
import "rsuite/dist/rsuite.min.css";
// Icons
import { FaBox, FaArrowDown, FaSearch } from "react-icons/fa";

const StockReturn = () => {
  const navigate = useNavigate();
  const { inventory, loadingInventory, startInventoryPolling, stopInventoryPolling } = useContext(MotherContext);
  const [formLoading, setFormLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [stockOutDate, setStockOutDate] = useState("");
  const [returnQuantities, setReturnQuantities] = useState({});

  useEffect(() => {
    startInventoryPolling();
    return () => stopInventoryPolling();
  }, [startInventoryPolling, stopInventoryPolling]);

  // ✅ Preserve user input: only add new items, don't reset existing
  useEffect(() => {
    setReturnQuantities((prev) => {
      const updated = { ...prev };
      inventory.forEach((item) => {
        if (item.id && !(item.id in updated)) {
          updated[item.id] = 0;
        }
      });
      return updated;
    });
  }, [inventory]);

  const handleQuantityChange = (id, value) => {
    const numValue = Number(value) || 0;
    const item = inventory.find((d) => d.id?.toString() === id.toString());
    const hasProduct = item?.has_product || 0;
    if (numValue > hasProduct) {
      toaster.push(
        <Message type="warning" duration={3000}>
          Cannot return more than available product: {hasProduct} {item?.product_unit}
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }
    setReturnQuantities((prev) => ({
      ...prev,
      [id]: numValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stockOutDate) {
      toaster.push(<Message type="error">Please select a date.</Message>);
      return;
    }

    const ids = [];
    const returnGoods = [];
    try {
      for (const [id, qty] of Object.entries(returnQuantities)) {
        const numQty = Number(qty) || 0;
        if (numQty <= 0) continue;
        const item = inventory.find((d) => d.id?.toString() === id.toString());
        if (!item) continue;
        const hasProduct = item?.has_product || 0;
        if (numQty > hasProduct) {
          toaster.push(
            <Message type="warning" duration={3000}>
              Cannot return {numQty} of "{item.item?.item_name || "Unknown"}". Max: {hasProduct}.
            </Message>,
            { placement: "topEnd" }
          );
          return;
        }
        ids.push(id);
        returnGoods.push(numQty);
      }
      if (ids.length === 0) {
        toaster.push(<Message type="warning">Please enter at least one return quantity.</Message>);
        return;
      }

      setFormLoading(true);
      const payload = new URLSearchParams();
      payload.append("stock_out_date", stockOutDate);
      ids.forEach((id) => payload.append("id[]", id));
      returnGoods.forEach((qty) => payload.append("return_goods[]", qty));

      const res = await AxiosAuthInstance.post("inventory/stockadd", payload, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (res.data.status === true) {
        toaster.push(<Message type="success">Goods returned successfully!</Message>);
        navigate("/stock_list");
      } else {
        toaster.push(<Message type="error">{res.data.message || "Failed to return goods."}</Message>);
      }
    } catch (err) {
      console.error("Return goods error:", err);
      const errorMsg =
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {})[0]?.[0] ||
        "Failed to return goods.";
      toaster.push(<Message type="error">{errorMsg}</Message>);
    } finally {
      setFormLoading(false);
    }
  };

  const filteredData = inventory.filter(
    (item) =>
      (item.category?.category_name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (item.receipt_no?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (item.item?.item_name?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "100px",
      center: true,
    },
    {
      name: "Item Name",
      selector: (row) => row.item?.item_name || "N/A",
      sortable: true,
      grow: 2,
      cell: (row) => <div className="fw-medium">{row.item?.item_name || "N/A"}</div>,
    },
    {
      name: "In Stock",
      selector: (row) => (row.main_stock || 0) - (row.stock_out || 0),
      sortable: true,
      width: "140px",
      cell: (row) => {
        const available = (row.main_stock || 0) - (row.stock_out || 0);
        return (
          <Badge
            color={available > 0 ? "green" : "red"}
            content={`${available} ${row.product_unit}`}
            style={{ fontSize: "0.85em" }}
          />
        );
      },
    },
    {
      name: "Return Quantity",
      width: "200px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Input
            type="number"
            min="0"
            max={row.has_product}
            step="1"
            placeholder="0"
            value={returnQuantities[row.id] || 0}
            onChange={(value) => handleQuantityChange(row.id, value)}
            style={{ width: 120 }}
          />
          <small className="text-muted">{row.product_unit}</small>
        </div>
      ),
    },
    {
      name: "Has Product",
      selector: (row) => row.has_product,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <Badge color="success" style={{ fontSize: "0.8em" }}>
          {row.has_product}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <Breadcrumb title="Return Goods" />
      <form onSubmit={handleSubmit}>
        <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <h5 className="text-dark mb-0">
            <FaArrowDown className="me-2" /> Return Goods
          </h5>
          <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
            <Input
              placeholder="Search by item, receipt, or category..."
              value={search}
              onChange={setSearch}
              prefix={<FaSearch />}
              style={{ width: 300 }}
              size="md"
            />
            <Input
              type="date"
              value={stockOutDate}
              onChange={setStockOutDate}
              style={{ width: 160 }}
              size="md"
            />
            <Button
              className="custom-bg"
              appearance="primary"
              startIcon={<FaArrowDown />}
              type="submit"
              disabled={formLoading}
              style={{ minWidth: 160 }}
            >
              {formLoading ? (
                <>
                  <Loader size="sm" /> Returning...
                </>
              ) : (
                "Return Goods"
              )}
            </Button>
          </div>
        </div>
        <DataTable
          keyField="id"
          columns={columns}
          data={filteredData}
          pagination
          progressPending={loadingInventory}
          progressComponent={
            <div className="d-flex justify-content-center my-5">
              <Loader size="md" /> <span className="ms-2">Loading inventory...</span>
            </div>
          }
          noDataComponent={
            <div className="text-center my-5 text-muted">
              <FaBox size={48} className="mb-3 text-secondary" />
              <h5>No inventory found</h5>
              <p>Try adjusting your search or add new stock.</p>
            </div>
          }
          paginationPerPage={100}
          paginationRowsPerPageOptions={[100, 250, 500, 1000]}
          highlightOnHover
          responsive
          fixedHeader
          fixedHeaderScrollHeight="600px"
          striped
        />
      </form>
    </>
  );
};

export default StockReturn;